"use client"

import { useState, useEffect } from "react"
import { supabase } from "../../supabaseClient"
import { useAuth } from "../../context/AuthContext"
import { ensureInstitutionOnPayload } from "../../lib/tenant"
import "../../styles/DisciplinaForm.css"

export default function DisciplinaForm({ onAdded, disciplinaParaEditar, onCancelEdit }) {
  const { isAdmin, instituicaoId } = useAuth()
  const [nome, setNome] = useState("")
  const [tipo, setTipo] = useState("G")
  const [importancia, setImportancia] = useState("geral")
  const [cargaHorariaSemanal, setCargaHorariaSemanal] = useState(2)
  const [loading, setLoading] = useState(false)
  const [isEditing, setIsEditing] = useState(false)

  const generateCodigo = (nomeDisciplina) => {
    const initials = nomeDisciplina
      .split(" ")
      .map((w) => w[0])
      .join("")
      .toUpperCase()
    const randomNum = Math.floor(100 + Math.random() * 900)
    return initials + randomNum
  }

  useEffect(() => {
    if (disciplinaParaEditar) {
      setIsEditing(true)
      setNome(disciplinaParaEditar.nome || "")
      setTipo(disciplinaParaEditar.tipo || "G")
      setImportancia(disciplinaParaEditar.importancia || "geral")
      setCargaHorariaSemanal(disciplinaParaEditar.carga_horaria_semanal || 2)
    } else {
      resetForm()
    }
  }, [disciplinaParaEditar])

  const resetForm = () => {
    setIsEditing(false)
    setNome("")
    setTipo("G")
    setImportancia("geral")
    setCargaHorariaSemanal(2)
  }

  useEffect(() => {
    if (tipo === "N") {
      if (importancia === "fundamental") {
        setCargaHorariaSemanal(8)
      } else if (importancia === "essencial") {
        setCargaHorariaSemanal(6)
      } else if (importancia === "chave") {
        setCargaHorariaSemanal(4)
      } else {
        setCargaHorariaSemanal(4)
      }
    } else {
      if (importancia === "fundamental") {
        setCargaHorariaSemanal(4)
      } else if (importancia === "essencial") {
        setCargaHorariaSemanal(4)
      } else {
        setCargaHorariaSemanal(2)
      }
    }
  }, [tipo, importancia])

  const handleSubmit = async (e) => {
    e.preventDefault()

    if (!nome.trim()) {
      alert("Preencha todos os campos!")
      return
    }

    setLoading(true)

    try {
      const dadosDisciplina = {
        nome,
        tipo,
        importancia,
        carga_horaria_semanal: cargaHorariaSemanal,
      }

      if (isEditing && disciplinaParaEditar) {
        const { error } = await supabase.from("disciplinas").update(dadosDisciplina).eq("id", disciplinaParaEditar.id)

        if (error) throw error
        if (onCancelEdit) onCancelEdit()
      } else {
        const payload = ensureInstitutionOnPayload({
          ...dadosDisciplina,
          codigo: generateCodigo(nome),
          created_at: new Date().toISOString(),
        }, isAdmin, instituicaoId)
        const { error } = await supabase.from("disciplinas").insert([payload])

        if (error) throw error
      }

      resetForm()
      if (onAdded) onAdded()
    } catch (err) {
      alert("Erro ao salvar disciplina: " + err.message)
      console.error(err)
    } finally {
      setLoading(false)
    }
  }

  const handleCancelar = () => {
    resetForm()
    if (onCancelEdit) onCancelEdit()
  }

  return (
    <form onSubmit={handleSubmit} className="disciplina-form-card">
      <h2 className="form-title">{isEditing ? "Editar Disciplina" : "Adicionar Nova Disciplina"}</h2>

      <div className="form-grid">
        <div className="form-group">
          <label>
            Nome da Disciplina <span className="required">*</span>
          </label>
          <input
            type="text"
            placeholder="Ex: Matemática, Física"
            value={nome}
            onChange={(e) => setNome(e.target.value)}
            className="form-input"
            required
          />
        </div>

        <div className="form-group tipo-disciplina-group">
          <label>Tipo da Disciplina</label>
          <div className="tipo-opcoes">
            <label className={`tipo-option ${tipo === "N" ? "selected nuclear" : ""}`}>
              <input
                type="radio"
                name="tipo"
                value="N"
                checked={tipo === "N"}
                onChange={(e) => setTipo(e.target.value)}
              />
              <span className="tipo-icon">N</span>
              <span className="tipo-label">Nuclear</span>
              <span className="tipo-desc">Mais tempo no horário</span>
            </label>
            <label className={`tipo-option ${tipo === "G" ? "selected global" : ""}`}>
              <input
                type="radio"
                name="tipo"
                value="G"
                checked={tipo === "G"}
                onChange={(e) => setTipo(e.target.value)}
              />
              <span className="tipo-icon">G</span>
              <span className="tipo-label">Global/Geral</span>
              <span className="tipo-desc">Tempo padrão</span>
            </label>
          </div>
        </div>

        <div className="form-group">
          <label>Grau de Importância</label>
          <select value={importancia} onChange={(e) => setImportancia(e.target.value)} className="form-input">
            <option value="fundamental">Fundamental (mais tempos)</option>
            <option value="essencial">Essencial</option>
            <option value="chave">Chave</option>
            <option value="geral">Geral (menos tempos)</option>
          </select>
        </div>

        <div className="form-group">
          <label>Tempos Semanais Sugeridos</label>
          <div className="calculated-value">
            <span className="value-number">{cargaHorariaSemanal}</span>
            <span className="value-label">tempos por semana</span>
          </div>
          <small className="form-hint">Calculado automaticamente. Pode ser alterado ao vincular na turma.</small>
        </div>
      </div>

      <div className="form-actions">
        {isEditing && (
          <button type="button" className="btn-cancelar" onClick={handleCancelar}>
            Cancelar
          </button>
        )}
        <button type="submit" className="btn-salvar" disabled={loading}>
          {loading ? "Salvando..." : isEditing ? "Atualizar" : "Salvar"}
        </button>
      </div>
    </form>
  )
}
